import React, { useEffect, useState, useRef } from "react";
import {
  GetPurchaseVoucherList,
  DeletePurchaseVoucher,
  ApprovePurchaseVoucher,
  ExportPurchaseVouchersToExcel,
} from "../../../Services/Services";
import config from "../../../config";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Modal,
  Box,
  TextField,
  MenuItem,
  Popover,
  Typography,
  Checkbox,
  Autocomplete,
  Grid,
  Chip,
  Menu,
  Divider,
  useTheme,
  useMediaQuery,
  Collapse,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Edit,
  Delete,
  Print,
  Visibility,
  Add,
  Close,
  ExpandMore,
  ExpandLess,
  Description as FileIcon,
} from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { generatePurchasePdf } from "../Accounts/PurchaseVoucherPrint";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

const PurchaseVouchers = () => {
  // ===== States =====
  const [purchaseVouchers, setPurchaseVouchers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [voucherToDelete, setVoucherToDelete] = useState(null);
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [PurchaseToApprove, setPurchaseToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);
  const [showRemarkForIndentId, setShowRemarkForIndentId] = useState(null);
  const baseURL = config.staticBaseURL;
  // Mobile Responsive States
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedPurchase, setExpandedPurchase] = useState(null);
  const [isExcelLoading, setExportLoading] = useState(false);

  // Date Range States
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [selectingDate, setSelectingDate] = useState(null);
  const dateInputRef = useRef(null);

  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  // Filters with date_range
  const [filters, setFilters] = useState({
    date_range: "today",
    po_no: "",
    supplier: "",
    invoice_date: "",
  });

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  // Add this function in your PurchaseVouchers component
  const handleExportToExcel = async () => {
    try {
      if (purchaseVouchers.length === 0) {
        toast.warning("No data available to export");
        return;
      }

      setExportLoading(true);
      const exportParams = { ...filters };

      // Add custom date range if selected
      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        exportParams.from_date = customDateRange.from;
        exportParams.to_date = customDateRange.to;
      }

      // Call the export service
      const blob = await ExportPurchaseVouchersToExcel(exportParams);

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with current date
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${(today.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;
      link.download = `purchase_vouchers_${dateStr}.xlsx`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      // Dismiss loading toast and show success
      toast.dismiss(isExcelLoading);
      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error(
        error.response?.data?.message || "Failed to export data to Excel",
      );
    }
  };

  // Helper function to format date display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // Get date range display text
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchVouchers(1, true, filters);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, customDateRange]);

  const fetchVouchers = async (
    pageNum = 1,
    reset = false,
    filterValues = filters,
  ) => {
    const params = { ...filterValues };

    if (
      filterValues.date_range === "custom" &&
      customDateRange.from &&
      customDateRange.to
    ) {
      params.from_date = customDateRange.from;
      params.to_date = customDateRange.to;
    }

    const data = await GetPurchaseVoucherList(params, pageNum);

    if (reset || pageNum === 1) {
      setPurchaseVouchers(
        Array.isArray(data) ? data.results : data.results || [],
      );
    } else {
      setPurchaseVouchers((prev) => [
        ...prev,
        ...(Array.isArray(data) ? data.results : data.results || []),
      ]);
    }

    setHasMore(Array.isArray(data) ? data.next !== null : data.next !== null);
    setPage(pageNum);
  };

  const fetchMoreData = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchVouchers(nextPage, false, filters);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFilters({
      date_range: "today",
      po_no: "",
      supplier: "",
      invoice_date: "",
    });
    setCustomDateRange({ from: "", to: "" });
    setSelectingDate(null);
    setPage(1);
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };

  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const handleDelete = async (voucherId) => {
    try {
      await DeletePurchaseVoucher(voucherId);
      toast.success("Deleted successfully!");
      fetchVouchers(page, true, filters);
    } catch (error) {
      console.error("Delete error:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Delete failed.");
    }
  };

  const handleApprove = async (voucher) => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApprovePurchaseVoucher(
        voucher.purchase_voucher_no,
        approvedById,
        voucher.status,
        remarkInput,
      );

      setPurchaseVouchers((prev) =>
        prev.map((item) =>
          item.purchase_voucher_no === updated.purchase_voucher_no
            ? updated
            : item,
        ),
      );

      // Show appropriate toast based on status
      if (voucher.status === "Approved") {
        toast.success("Purchase Voucher approved successfully!");
      } else if (voucher.status === "Rejected") {
        toast.error("Purchase Voucher rejected!");
      }
    } catch (error) {
      console.error("Approval Error:", error);

      if (voucher.status === "Approved") {
        toast.error("Failed to approve Purchase Voucher.");
      } else if (voucher.status === "Rejected") {
        toast.error("Failed to reject Purchase Voucher.");
      }
    }
  };

  const userRole = localStorage.getItem("role");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);
  return (
    <>
      <ToastContainer />
      {/* Filter Section */}
      <Box sx={{ mt: { xs: 3.5, sm: -5 }, pt: -3, mb: 2, width: "100%" }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Dropdown */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                  customDateRange.from &&
                  customDateRange.to
                  ? `${formatDateDisplay(
                    customDateRange.from,
                  )} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                    ? "Today"
                    : filters.date_range === "yesterday"
                      ? "Yesterday"
                      : filters.date_range === "this_week"
                        ? "This Week"
                        : filters.date_range === "previous_week"
                          ? "Previous Week"
                          : filters.date_range === "this_month"
                            ? "This Month"
                            : filters.date_range === "current_year"
                              ? "Current Year"
                              : filters.date_range === "financial_year"
                                ? "Financial Year"
                                : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                fontWeight: "bold",
                "& .MuiInputBase-input": {
                  fontSize: "0.8rem",
                  padding: "4px 8px",
                },
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.6rem",
                },
              }}
            />
          </Grid>

          {/* Purchase Order No */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Purchase Order No"
              name="po_no"
              size="small"
              value={filters.po_no}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          {/* Supplier */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Supplier"
              name="supplier"
              size="small"
              value={filters.supplier}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          {/* Invoice Date */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Invoice Date"
              name="invoice_date"
              type="date"
              size="small"
              value={filters.invoice_date}
              onChange={handleFilterChange}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          {/* Reset Button - Mobile: 50% width */}
          <Grid item xs={6} sm={1}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              Reset
            </Button>
          </Grid>


          <Grid item xs={6} sm={1}>
            <Button
              variant="contained"
              color="success"
              onClick={handleExportToExcel}
              disabled={isExcelLoading || purchaseVouchers.length === 0}
              fullWidth
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              {isExcelLoading ? (
                <CircularProgress size={16} color="inherit" sx={{ mr: 0.5 }} />
              ) : (
                <FileIcon fontSize="small" />
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>
      {/* Date Range Menu - UPDATED */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 500,
            "& .MuiTypography-root": {
              fontSize: "0.7rem",
            },
          },
        }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setFilters((prev) => ({ ...prev, date_range: range.key }));
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}
          >
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max:
                    tempCustomDateRange.to ||
                    new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>

      {/* InfiniteScroll with Mobile/Desktop Views */}
      <InfiniteScroll
        dataLength={purchaseVouchers.length}
        next={fetchMoreData}
        hasMore={hasMore}
        height={isMobile ? "70vh" : "90vh"}
        // scrollableTarget={isMobile ? undefined : "scrollableDiv"}
        loader={<h4>Loading...</h4>}
      >
        {isMobile ? (
          /* 📱 MOBILE VIEW */
          <Box sx={{ bgcolor: "background.paper" }}>
            {purchaseVouchers.map((voucher, index) => (
              <Box key={voucher.id || index}>
                <Box
                  onClick={() =>
                    setExpandedPurchase(
                      expandedPurchase === voucher.purchase_voucher_no
                        ? null
                        : voucher.purchase_voucher_no,
                    )
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:active": {
                      bgcolor: "#e3f2fd",
                    },
                    borderLeft: `4px solid ${voucher.status === "Approved"
                      ? "#4caf50"
                      : voucher.status === "Rejected"
                        ? "#f44336"
                        : "#ff9800"
                      }`,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#666",
                          minWidth: 25,
                        }}
                      >
                        #{index + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          flex: 1,
                        }}
                      >
                        {voucher.purchase_number}
                      </Typography>
                      {/* <Chip
                        label={
                          voucher.status === "Approved"
                            ? "✓"
                            : voucher.status === "Rejected"
                            ? "✗"
                            : "⏱"
                        }
                        color={
                          voucher.status === "Approved"
                            ? "success"
                            : voucher.status === "Rejected"
                            ? "error"
                            : "warning"
                        }
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 30,
                          fontSize: "0.7rem",
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      /> */}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#1976d2",
                        }}
                      >
                        ₹
                        {Number(voucher.total_amount || 0).toLocaleString(
                          "en-IN",
                        )}
                      </Typography>
                      <Chip
                        label={
                          voucher.payment_status === "Paid"
                            ? "Paid"
                            : voucher.payment_status === "Unpaid"
                              ? "Unpaid"
                              : "Partial"
                        }
                        color={
                          voucher.payment_status === "Paid"
                            ? "success"
                            : voucher.payment_status === "Unpaid"
                              ? "error"
                              : "warning"
                        }
                        size="small"
                        sx={{
                          // fontSize: "0.2rem", // 👈 font chhota hoga
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </Box>
                  </Box>

                  <IconButton size="small" sx={{ ml: 1 }}>
                    {expandedPurchase === voucher.purchase_voucher_no ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                <Collapse
                  in={expandedPurchase === voucher.purchase_voucher_no}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                      borderLeft: `4px solid ${voucher.status === "Approved"
                        ? "#4caf50"
                        : voucher.status === "Rejected"
                          ? "#f44336"
                          : "#ff9800"
                        }`,
                    }}
                  >
                    {/* Amount Details Grid */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 0.8,
                        mb: 1,
                        p: 1,
                        bgcolor: "#e3f2fd",
                        borderRadius: "4px",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#1976d2",
                            letterSpacing: "0.5px",
                          }}
                        >
                          TOTAL
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#1976d2",
                          }}
                        >
                          ₹
                          {Number(voucher.total_amount || 0).toLocaleString(
                            "en-IN",
                          )}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#2e7d32",
                            letterSpacing: "0.5px",
                          }}
                        >
                          PAID
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#2e7d32",
                          }}
                        >
                          ₹
                          {Number(voucher.receipt_amount || 0).toLocaleString(
                            "en-IN",
                          )}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#d32f2f",
                            letterSpacing: "0.5px",
                          }}
                        >
                          BALANCE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            color: "#d32f2f",
                          }}
                        >
                          ₹
                          {Number(voucher.balance_amount || 0).toLocaleString(
                            "en-IN",
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Details Grid */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.8,
                        mb: 1,
                      }}
                    >
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          PROJECT
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "#333",
                          }}
                        >
                          {voucher.project_name || "N/A"}
                        </Typography>
                      </Box>

                      {/* <Box> */}
                      {/* <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          DIVISION
                        </Typography> */}
                      {/* <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.division_name || "N/A"}
                        </Typography> */}
                      {/* </Box> */}

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          DATE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {formatDate(voucher.created_at)}
                        </Typography>
                      </Box>
                      {/* 
                      <Box> */}
                      {/* <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          INDENT NO
                        </Typography> */}
                      {/* <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.indent_no || "N/A"}
                        </Typography> */}
                      {/* </Box> */}

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          PURCHASE ORDER
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.purchase_order || "N/A"}
                        </Typography>
                      </Box>

                      {voucher.transporter && (
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            TRANSPORTER
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {voucher.transporter}
                          </Typography>
                        </Box>
                      )}

                      {voucher.lr_no && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            LR NO
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {voucher.lr_no}
                          </Typography>
                        </Box>
                      )}

                      {voucher.vehicle_no && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            VEHICLE NO
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {voucher.vehicle_no}
                          </Typography>
                        </Box>
                      )}

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          CREATED BY
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {voucher.created_by_name || "N/A"}
                        </Typography>
                      </Box>

                      {voucher.approved_name && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "#999",
                              letterSpacing: "0.5px",
                            }}
                          >
                            APPROVED BY
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              fontWeight: 500,
                              color: "#555",
                            }}
                          >
                            {voucher.approved_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {voucher.remarks && (
                      <Box sx={{ mb: 0.8 }}>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                            mb: 0.3,
                          }}
                        >
                          REMARKS
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "#555",
                            fontStyle: "italic",
                            lineHeight: 1.3,
                          }}
                        >
                          {voucher.remarks}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {userRole != "Staff" && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setApproveAnchorEl(true);
                            setPurchaseToApprove(voucher);
                          }}
                          sx={{
                            bgcolor: "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": { bgcolor: "#bbdefb" },
                          }}
                        >
                          <Visibility sx={{ fontSize: "1rem", color: "#1976d2" }} />
                        </IconButton>
                      )}
                      <Box sx={{ display: "flex", gap: 0.6 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const route = voucher.purchase_order
                              ? "/dashboard/pe-with-po"
                              : "/dashboard/pe-without-po";
                            navigate(route, {
                              state: {
                                issue: {
                                  ...voucher,
                                  id: voucher.purchase_voucher_no,
                                },
                              },
                            });
                          }}
                          disabled={voucher.status === "Approved"}
                          sx={{
                            bgcolor: "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#bbdefb",
                              transform: "scale(1.05)",
                            },
                            "&:disabled": {
                              bgcolor: "#f5f5f5",
                              opacity: 0.5,
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Edit
                            sx={{
                              fontSize: "1rem",
                              color: "#1976d2",
                              transform: "scale(0.6)",
                            }}
                          />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                            setVoucherToDelete(voucher.purchase_voucher_no);
                          }}
                          disabled={voucher.status === "Approved"}
                          sx={{
                            bgcolor: "#ffebee",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#ffcdd2",
                              transform: "scale(1.05)",
                            },
                            "&:disabled": {
                              bgcolor: "#f5f5f5",
                              opacity: 0.5,
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Delete
                            sx={{
                              fontSize: "1rem",
                              color: "#d32f2f",
                              transform: "scale(0.6)",
                            }}
                          />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            generatePurchasePdf(voucher);
                          }}
                          sx={{
                            bgcolor: "#fff3e0",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#ffe0b2",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Print
                            sx={{
                              fontSize: "1rem",
                              color: "#f57c00",
                              transform: "scale(0.6)",
                            }}
                          />
                        </IconButton>

                        {typeof voucher.attachment === "string" ? (
                          <a
                            href={`${voucher.attachment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                bgcolor: "#e8f5e9",
                                borderRadius: "6px",
                                width: 32,
                                height: 32,
                                "&:hover": {
                                  bgcolor: "#c8e6c9",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s",
                              }}
                            >
                              <Visibility
                                sx={{
                                  fontSize: "1rem",
                                  color: "#2e7d32",
                                  transform: "scale(0.6)",
                                }}
                              />
                            </IconButton>
                          </a>
                        ) : voucher.attachment instanceof File ? (
                          <a
                            href={URL.createObjectURL(`${baseURL}${voucher.attachment}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                bgcolor: "#e8f5e9",
                                borderRadius: "6px",
                                width: 32,
                                height: 32,
                                "&:hover": {
                                  bgcolor: "#c8e6c9",
                                  transform: "scale(1.05)",
                                },
                                transition: "all 0.2s",
                              }}
                            >
                              <Visibility
                                sx={{
                                  fontSize: "1rem",
                                  color: "#2e7d32",
                                  transform: "scale(0.6)",
                                }}
                              />
                            </IconButton>
                          </a>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                </Collapse>

                <Divider />
              </Box>
            ))}

            {/* Mobile Total Summary */}
            <Box
              sx={{
                p: 2,
                bgcolor: "#e3f2fd",
                position: "sticky",
                bottom: 0,
                borderTop: "2px solid #1976d2",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#1976d2",
                  }}
                >
                  Total Amount:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#1976d2",
                  }}
                >
                  ₹
                  {purchaseVouchers
                    .reduce(
                      (sum, v) => sum + parseFloat(v.total_amount || 0),
                      0,
                    )
                    .toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#2e7d32",
                  }}
                >
                  Total Paid:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#2e7d32",
                  }}
                >
                  ₹
                  {purchaseVouchers
                    .reduce(
                      (sum, v) => sum + parseFloat(v.receipt_amount || 0),
                      0,
                    )
                    .toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#d32f2f",
                  }}
                >
                  Total Balance:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "#d32f2f",
                  }}
                >
                  ₹
                  {purchaseVouchers
                    .reduce(
                      (sum, v) => sum + parseFloat(v.balance_amount || 0),
                      0,
                    )
                    .toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (





























          /* 💻 DESKTOP VIEW */
          <TableContainer component={Paper} sx={{ overflow: "unset" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1600,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      whiteSpace: "nowrap",
                      fontSize: "0.7rem",
                    },
                  }}
                >
                  {userRole != "Staff" && (
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fafafa",
                        zIndex: 3,
                      }}
                    >
                      View
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: userRole != "Staff" ? 50 : 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    S.No.
                  </TableCell>

                  <TableCell>Voucher No</TableCell>
                  {/* <TableCell>Status</TableCell> */}
                  <TableCell>Supplier</TableCell>
                  <TableCell>Invoice No.</TableCell>
                  <TableCell>Invoice Date</TableCell>
                  <TableCell>Total Amt</TableCell>
                  {/* <TableCell>Paid Amt</TableCell>
                  <TableCell>Bal Amt</TableCell> */}
                  <TableCell>Project</TableCell>
                  {/* <TableCell>Indent</TableCell> */}
                  <TableCell>Purchase Order</TableCell>
                  {/* <TableCell>Division</TableCell> */}
                  {/* <TableCell>Transporter Name</TableCell> */}
                  <TableCell>LR No</TableCell>
                  <TableCell>Vehicle No</TableCell>
                  <TableCell>Payment Status</TableCell>
                  <TableCell>Date</TableCell>

                  <TableCell>Created By</TableCell>
                  {/* <TableCell>Approved By</TableCell> */}
                  {/* <TableCell>Remarks</TableCell> */}
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {purchaseVouchers.map((voucher, index) => (
                  <TableRow
                    hover
                    key={index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        "& td": {
                          backgroundColor: "#e3f2fd !important",
                        },
                      },
                      "& td": {
                        paddingRight: "13px !important",
                        paddingLeft: "13px !important",
                        padding: "0px",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        whiteSpace: "nowrap",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                      },
                    }}
                  >
                    {userRole != "Staff" && (
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: "#fafafa",
                          // zIndex: 3,
                        }}
                      >
                        {/* // ✅ Replace karo */}
                        <IconButton
                          size="small"
                          onClick={() => {
                            setApproveAnchorEl(true);
                            setPurchaseToApprove(voucher);
                          }}
                          sx={{ padding: 0 }}
                        >
                          <Visibility sx={{ fontSize: "1rem", color: "#1976d2" }} />
                        </IconButton>
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: userRole != "Staff" ? 50 : 0,
                        backgroundColor: "#f5f5f5",
                        // zIndex: 3,
                      }}
                    >
                      {index + 1}
                    </TableCell>

                    <TableCell>{voucher.purchase_number}</TableCell>
                    {/* <TableCell align="center">
                      <Chip
                        label={
                          voucher.status === "Approved"
                            ? "Approved"
                            : voucher.status === "Rejected"
                              ? "Rejected"
                              : "Pending"
                        }
                        color={
                          voucher.status === "Approved"
                            ? "success"
                            : voucher.status === "Rejected"
                              ? "error"
                              : "warning"
                        }
                        variant="outlined"
                        size="small"
                        sx={{
                          // fontSize: "0.2rem", // 👈 font chhota hoga
                          height: "10px",
                          transform: "scale(0.8)",
                          "& .MuiChip-label": {
                            padding: "0 6px",
                          },
                        }}
                      />
                    </TableCell> */}
                    <TableCell>{voucher.supplier_name}</TableCell>
                    <TableCell>{voucher.invoice_number}</TableCell>
                    <TableCell>{voucher.invoice_date}</TableCell>
                    <TableCell>
                      {Number(voucher.final_amount || 0).toLocaleString(
                        "en-IN",
                      )}
                    </TableCell>
                    {/* <TableCell>
                      {Number(voucher.receipt_amount || 0).toLocaleString(
                        "en-IN",
                      )}
                    </TableCell>
                    <TableCell>
                      {Number(voucher.balance_amount || 0).toLocaleString(
                        "en-IN",
                      )}
                    </TableCell> */}
                    <TableCell>
                      {voucher.project_name?.length > 30 ? (
                        <Tooltip title={voucher.project_name} arrow>
                          <span>
                            {voucher.project_name.substring(0, 30)}...
                          </span>
                        </Tooltip>
                      ) : (
                        voucher.project_name
                      )}
                    </TableCell>
                    {/* <TableCell>{voucher.indent_no}</TableCell> */}
                    <TableCell>{voucher.purchase_order}</TableCell>
                    {/* <TableCell>{voucher.division_name}</TableCell> */}
                    {/* <TableCell>{voucher.transporter}</TableCell> */}
                    <TableCell>{voucher.lr_no}</TableCell>
                    <TableCell>{voucher.vehicle_no}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          voucher.payment_status === "Paid"
                            ? "Paid"
                            : voucher.payment_status === "Unpaid"
                              ? "Unpaid"
                              : "Partial"
                        }
                        color={
                          voucher.payment_status === "Paid"
                            ? "success"
                            : voucher.payment_status === "Unpaid"
                              ? "error"
                              : "warning"
                        }
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>

                    <TableCell>{formatDate(voucher.created_at)}</TableCell>

                    <TableCell>{voucher.created_by_name}</TableCell>
                    {/* <TableCell>{voucher.approved_name}</TableCell> */}
                    {/* <TableCell>
                      <Tooltip title={voucher.remarks || ""} arrow>
                        <span>{truncateText(voucher.remarks || "", 20)}</span>
                      </Tooltip>
                    </TableCell> */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#f5f5f5",
                        // zIndex: 3,
                        minWidth: 180,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >

                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          size="small"
                          color="primary"
                          onClick={() => {
                            const route = voucher.purchase_order
                              ? "/dashboard/pe-with-po"
                              : "/dashboard/pe-without-po"; navigate(route, {
                                state: {
                                  issue: {
                                    ...voucher,
                                    id: voucher.purchase_voucher_no,
                                  },
                                },
                              });
                          }}
                        // disabled={voucher.status === "Approved"}
                        >
                          <Edit transform="scale(0.6)" />
                        </IconButton>

                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          size="small"
                          color="error"
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                            setVoucherToDelete(voucher.purchase_voucher_no);
                          }}
                        // disabled={voucher.status === "Approved"}
                        >
                          <Delete transform="scale(0.6)" />
                        </IconButton>

                        <IconButton
                          sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                          color="warning"
                          size="small"
                          onClick={() => generatePurchasePdf(voucher)}
                        >
                          <Print transform="scale(0.6)" />
                        </IconButton>
                        <span
                          style={{
                            display: "inline-block",
                            width: 36,
                            textAlign: "center",
                          }}
                        >
                          {voucher?.attachment ? (
                            <a
                              href={`${baseURL}${voucher.attachment}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <IconButton
                                sx={{
                                  padding: 0,
                                  paddingLeft: 1,
                                  paddingRight: 1,
                                }}
                                color="info"
                                size="small"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </a>
                          ) : (
                            <span style={{ display: "inline-block", width: 24 }} />
                          )}
                        </span>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow
                  sx={{
                    "& td": {
                      backgroundColor: "#E3F2FD !important",
                      color: "#000 !important",
                      fontWeight: "bold !important",
                    },
                  }}
                >
                  <TableCell colSpan={6} align="right">
                    Totals:
                  </TableCell>
                  <TableCell>
                    {purchaseVouchers
                      .reduce(
                        (sum, v) => sum + parseFloat(v.final_amount || 0),
                        0,
                      )
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell>
                  {/* <TableCell>
                    {purchaseVouchers
                      .reduce(
                        (sum, v) => sum + parseFloat(v.receipt_amount || 0),
                        0,
                      )
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell> */}
                  {/* <TableCell>
                    {purchaseVouchers
                      .reduce(
                        (sum, v) => sum + parseFloat(v.balance_amount || 0),
                        0,
                      )
                      .toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </TableCell> */}
                  <TableCell colSpan={13}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>
      {/* Delete Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setVoucherToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure ?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={async () => {
                if (voucherToDelete) {
                  await handleDelete(voucherToDelete);
                }
                setAnchorEl(null);
                setVoucherToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setVoucherToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Approve Popover */}
      <Popover
        open={Boolean(approveAnchorEl)}
        onClose={() => {
          setApproveAnchorEl(null);
          setPurchaseToApprove(null);
          setRemarkInput("");
          setShowRemarkForIndentId(null);
          setActionType(null);
        }}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            width: 1300, // Increased from 950
            maxHeight: "90vh", // Increased from 85vh
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        {PurchaseToApprove && (
          <>
            {/* ================= FIXED HEADER ================= */}
            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: "1px solid #e0e0e0",
                backgroundColor: "#f4f6f8",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                Purchase Voucher Preview
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Voucher No: {PurchaseToApprove.purchase_number}
              </Typography>
            </Box>

            {/* ================= SCROLLABLE CONTENT ================= */}
            <Box
              sx={{
                p: 2,
                overflowY: "auto",
                flex: 1,
                backgroundColor: "#fafafa",
              }}
            >
              {/* INFO SECTION */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 0,
                  border: "1px solid #000",
                  backgroundColor: "#fff",
                  mb: 2,
                }}
              >
                {/* Column 1 */}
                <Box sx={{ p: 1.5, borderRight: "1px solid #000" }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>VOUCHER NO.</strong>:{" "}
                    {PurchaseToApprove.purchase_number}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>PROJECT</strong>: {PurchaseToApprove.project_name}
                  </Typography>
                  {/* <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>INDENT</strong>: {PurchaseToApprove.indent_no}
                  </Typography> */}
                  {/* <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>DIVISION</strong>: {PurchaseToApprove.division_name}
                  </Typography> */}
                  {/* <Typography variant="body2">
                    <strong>TRANSPORTER</strong>:{" "}
                    {PurchaseToApprove.transporter}
                  </Typography> */}
                </Box>

                {/* Column 2 */}
                <Box sx={{ p: 1.5, borderRight: "1px solid #000" }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>SUPPLIER</strong>: {PurchaseToApprove.supplier_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>LR NO.</strong>: {PurchaseToApprove.lr_no}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>VEHICLE NO.</strong>: {PurchaseToApprove.vehicle_no}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>INVOICE NO.</strong>:{" "}
                    {PurchaseToApprove.invoice_number}
                  </Typography>
                  {/* <Typography variant="body2">
                    <strong>STATUS</strong>: {PurchaseToApprove.status}
                  </Typography> */}
                </Box>

                {/* Column 3 */}
                <Box sx={{ p: 1.5 }}>
                  {/* <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>REMARKS</strong>: {PurchaseToApprove.remarks || "-"}
                  </Typography> */}
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>PAYMENT STATUS</strong>:{" "}
                    {PurchaseToApprove.payment_status}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>CREATED BY</strong>:{" "}
                    {PurchaseToApprove.created_by_name}
                  </Typography>
                  {/* <Typography variant="body2">
                    <strong>APPROVED BY</strong>:{" "}
                    {PurchaseToApprove.approved_by || "-"}
                  </Typography> */}
                </Box>
              </Box>

              {/* ITEMS TABLE */}
              <TableContainer component={Box} sx={{ border: "1px solid #000" }}>
                <Table
                  size="small"
                  sx={{
                    "& .MuiTableBody-root .MuiTableRow-root": {
                      borderBottom: "1px solid #ccc",
                    },
                    "& .MuiTableCell-root": {
                      borderBottom: "1px solid #ccc",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        Sl.
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        Category
                      </TableCell>
                      {/* <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        Sub Category
                      </TableCell> */}
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        Item
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        Product Type
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        UOM
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        PO Qty
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        Rec Qty
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        Rate
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        Amount
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        GST %
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        GST Amt
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, py: 1, whiteSpace: "nowrap" }}
                      >
                        Total Amt
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {PurchaseToApprove.items?.map((item, index) => (
                      <TableRow
                        key={item.id}
                        sx={{
                          "&:nth-of-type(even)": { backgroundColor: "#fafafa" },
                        }}
                      >
                        <TableCell sx={{ py: 0.75 }}>{index + 1}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.category_name}
                        </TableCell>
                        {/* <TableCell sx={{ py: 0.75 }}>
                          {item.sub_category_name}
                        </TableCell> */}
                        <TableCell sx={{ py: 0.75 }}>
                          {item.item_name}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.size_name}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.unit_name}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>{item.quantity}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.received_qty}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>₹{item.rate}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>₹{item.amount}</TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          {item.gst_percentage}%
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          ₹{item.gst_amount}
                        </TableCell>
                        <TableCell sx={{ py: 0.75 }}>
                          ₹{item.item_total}
                        </TableCell>
                      </TableRow>
                    ))}

                    {/* ================= TOTALS ROW ================= */}
                    <TableRow
                      sx={{
                        backgroundColor: "#e8f5e9",
                        borderTop: "2px solid #000",
                      }}
                    >
                      <TableCell
                        colSpan={8}
                        align="right"
                        sx={{ fontWeight: 700, py: 1 }}
                      >
                        TOTAL :
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}>
                        ₹{PurchaseToApprove.total_amount?.toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}></TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}>
                        ₹{PurchaseToApprove.gst_amount?.toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1 }}>
                        ₹{PurchaseToApprove.final_amount?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* ================= FIXED FOOTER ================= */}
            <Box
              sx={{
                borderTop: "1px solid #e0e0e0",
                px: 2,
                py: 1.5,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                backgroundColor: "#fff",
                flexShrink: 0,
                gap: 1,
              }}
            >
              {/* REMARK FIELD */}
              {/* <TextField
                label="Remark"
                size="small"
                value={remarkInput}
                onChange={(e) => setRemarkInput(e.target.value)}
                placeholder="Enter remark..."
                sx={{ flex: 1 }}
              /> */}

              {/* APPROVE BUTTON */}
              {/* <Button
                variant="contained"
                color="success"
                size="small"
                onClick={async () => {
                  if (!remarkInput.trim()) {
                    toast.error("Remark is required!");
                    return;
                  }
                  await handleApprove({
                    ...PurchaseToApprove,
                    status: "Approved",
                    remarks: remarkInput,
                  });
                  // Close popover after approve */}
              {/* setApproveAnchorEl(null);
                  setPurchaseToApprove(null);
                  setRemarkInput("");
                  setShowRemarkForIndentId(null);
                  setActionType(null);
                }}
              >
                Approve
              </Button> */}

              {/* REJECT BUTTON */}
              {/* <Button
                variant="contained"
                color="error"
                size="small"
                onClick={async () => {
                  if (!remarkInput.trim()) {
                    toast.error("Remark is required!");
                    return;
                  }
                  await handleApprove({
                    ...PurchaseToApprove,
                    status: "Rejected",
                    remarks: remarkInput,
                  });
                  // Close popover after reject */}
              {/* setApproveAnchorEl(null);
                  setPurchaseToApprove(null);
                  setRemarkInput("");
                  setShowRemarkForIndentId(null);
                  setActionType(null);
                }}
                disabled={PurchaseToApprove?.status === "Approved"}
              >
                Reject
              </Button> */}

              {/* EDIT BUTTON */}
              {/* <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const route = PurchaseToApprove.purchase_order
                    ? "/dashboard/pe-with-po"
                    : "/dashboard/pe-without-po";
                  navigate(route, {
                    state: {
                      issue: {
                        ...PurchaseToApprove,
                        id: PurchaseToApprove.purchase_voucher_no,
                      },
                    },
                  });
                  setApproveAnchorEl(null);
                  setPurchaseToApprove(null);
                  setRemarkInput("");
                }}
                disabled={PurchaseToApprove?.status === "Approved"}
              >
                Edit
              </Button> */}

              {/* CLOSE BUTTON */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setApproveAnchorEl(null);
                  setPurchaseToApprove(null);
                  setRemarkInput("");
                  setShowRemarkForIndentId(null);
                  setActionType(null);
                }}
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
};

export default PurchaseVouchers;
